import { showLoading, hideLoading } from 'react-redux-loading-bar';
import get from 'lodash/get';
import omit from 'lodash/omit';

import { shouldFetchItems } from './selectors';
import { mapPhotos, transformPhotosToImage } from '../../common/helpers/photos';
import HttpV2 from '../../httpv2';
import * as types from './constants';
import { getShopBrandId } from '../../auth/selectors';

const ITEMS_PER_PAGE = 500;
const DEFAULT_INCLUDE = 'photos';

const fetchV2Items = (url, skip = 0, include = DEFAULT_INCLUDE) => dispatch =>
  HttpV2.get(url, { include, limit: ITEMS_PER_PAGE, skip })
    .then((response) => {
      const items = get(response, 'rows', []);
      dispatch({
        type: types.FETCH_ITEMS,
        payload: mapPhotos(items),
      });
      return (items.length === ITEMS_PER_PAGE);
    });

const fetchAllItems = endpoint => async (dispatch, getState) => {
  if (!shouldFetchItems(getState())) return;
  dispatch({ type: types.CLEAR_ITEMS });
  dispatch(showLoading());
  let skip = 0;
  let fetching = true;
  while (fetching) {
    fetching = await dispatch(fetchV2Items(endpoint, skip)); // eslint-disable-line
    skip += ITEMS_PER_PAGE;
  }
  dispatch(hideLoading());
};

export const fetchBrandItems = brandId => fetchAllItems(`/brand/${brandId}/product-brand`);

const fetchItem = url => dispatch =>
  HttpV2.get(url, { include: DEFAULT_INCLUDE })
    .then(response =>
      dispatch({
        type: types.FETCH_ITEM,
        payload: transformPhotosToImage(response),
      })
    );

export const fetchBrandItem = (brandId, id) => fetchItem(`/brand/${brandId}/product-brand/${id}`);

const addPhoto = (brandId, productBrandId, image) =>
  HttpV2.postRaw(`/brand/${brandId}/product-brand/${productBrandId}/photo`, { image });
const deletePhoto = (brandId, productBrandId, photoId) =>
  HttpV2.delete(`/brand/${brandId}/product-brand/${productBrandId}/photo/${photoId}`);
const updatePhoto = (brandId, productBrandId, photoId, image) =>
  HttpV2.putRaw(`/brand/${brandId}/product-brand/${productBrandId}/photo/${photoId}`, { image });

const addItemAction = payload => ({
  type: types.ADD_ITEM,
  payload,
});

const addItem = (url, data) => (dispatch, getState) =>
  HttpV2.post(url, omit(data, 'image'))
    .then((response) => {
      if (data.image) {
        const brandId = getShopBrandId(getState());
        return addPhoto(brandId, response.id, data.image)
          .then(
            photoResponse => dispatch(addItemAction({
              ...response,
              image: photoResponse.url,
            }))
          );
      }
      return dispatch(addItemAction(response));
    });

export const addBrandItem = (brandId, data) => addItem(`/brand/${brandId}/product-brand`, data);

const updateItemAction = payload => ({
  type: types.UPDATE_ITEM,
  payload,
});

const updateItem = (url, id, data) => (dispatch, getState) =>
  HttpV2.put(url, omit(data, ['image', 'photos']))
    .then((response) => {
      if (data.image instanceof File) {
        const brandId = getShopBrandId(getState());
        const existentPhotoId = get(data, 'photos.0.id');
        const deletePromise = existentPhotoId ? deletePhoto(brandId, id, existentPhotoId) : Promise.resolve();
        return deletePromise.then(() => addPhoto(brandId, id, data.image))
          .then(photoResponse => dispatch(updateItemAction({
            ...response,
            image: photoResponse.url,
          })));
      }

      return dispatch(updateItemAction(response));
    });
export const updateBrandItem =
  (brandId, id, data) => updateItem(`/brand/${brandId}/product-brand/${id}`, id, data);

const deleteItem = (url, id) => dispatch =>
  HttpV2.delete(url)
    .then(() =>
      dispatch({
        type: types.DELETE_ITEM,
        payload: id,
      })
    );
export const deleteBrandItem = (brandId, id) => deleteItem(`/brand/${brandId}/product-brand/${id}`, id);
